import React, { memo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { displays } from 'investira.react.lib';
import { validators } from 'investira.sdk';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  LoadingButton,
  IconButton,
  Icon,
  Button
} from 'investira.react.components';

import { PessoaImage } from '../../../components/molecules';
import { SearchAdd } from '../../../components/organisms';
import services from '../../../services';

const xValidationSchema = Yup.object().shape({
  pessoa_id: Yup.number().required('Selecione a pessoa Feeder'),
  master_pessoa_id: Yup.number()
    .required('Selecione a pessoa Master')
    .test('different-master', 'Feeder e Master devem ser pessoas diferentes', function (xMasterId) {
      return xMasterId !== this.parent.pessoa_id;
    })
});

const xInitialValues = {
  pessoa_id: null,
  master_pessoa_id: null,
  feeder: null,
  master: null
};

const FeederMasterDialog = memo(props => {
  const { open, onClose, onSubmitSuccess, onMessageSuccess, defaultValues } = props;
  const isEdit = !validators.isEmpty(defaultValues);

  useEffect(() => {
    if (isEdit) {
      if (defaultValues?.feeder) {
        handleSelectPessoa('pessoa_id', defaultValues.feeder);
      }
      if (defaultValues?.master) {
        handleSelectPessoa('master_pessoa_id', defaultValues.master);
      }
    } else {
      handleClearPessoa('pessoa_id');
      handleClearPessoa('master_pessoa_id');
    }
  }, [defaultValues]);

  const formik = useFormik({
    initialValues: xInitialValues,
    validationSchema: xValidationSchema,
    onSubmit: (pValues, pActions) => {
      onMessageSuccess('Vinculação Feeder/Master salva com sucesso!');
      onSubmitSuccess({
        pessoa_id: pValues.pessoa_id,
        master_pessoa_id: pValues.master_pessoa_id,
        feeder: pValues.feeder,
        master: pValues.master
      });
      pActions.resetForm();
      pActions.setSubmitting(false);
      onClose();
    }
  });

  const handleSelectPessoa = (pField, pData) => {
    formik.setFieldValue(pField, pData.pessoa_id, true);
    formik.setFieldValue(pField === 'pessoa_id' ? 'feeder' : 'master', pData, false);
  };

  const handleClearPessoa = pField => {
    formik.setFieldValue(pField, null, true);
    formik.setFieldValue(pField === 'pessoa_id' ? 'feeder' : 'master', null, false);
  };

  const handleClose = (_, pReason) => {
    if (formik.isSubmitting) {
      return;
    }

    if (pReason === 'backdropClick' || pReason === 'escapeKeyDown') {
      return;
    }

    formik.setSubmitting(false);
    formik.resetForm();
    onClose();
  };

  const renderSelected = (pKey, pLabel, pData, pOnClear) => {
    if (validators.isEmpty(pData)) {
      return null;
    }

    // Em caso de edição, o feeder não pode ser alterado
    const isDisabled = isEdit && pKey === 'feeder';

    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={1}
        sx={{
          px: 2,
          py: 1.5,
          border: '1px solid',
          borderColor: 'background.main',
          borderRadius: 1
        }}>
        <Stack gap={1}>
          <Typography variant="caption" color="textSecondary">
            {pLabel}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <PessoaImage
              logoPessoaId={pData.logo_pessoa_id}
              alt={pData.pessoa}
              hasLogo={pData.logo_ok}
              size={36}
            />
            <Stack>
              <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600 }}>
                {pData.pessoa_fantasia || pData.pessoa}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Typography variant="caption" color="textSecondary">
                  {pData.pessoa}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {displays.cnpj(pData.cnpj)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>

        <IconButton onClick={pOnClear} color="error" disabled={isDisabled}>
          <Icon iconName="remove" size={21} />
        </IconButton>
      </Stack>
    );
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={handleClose}
      disableEscapeKeyDown={formik.isSubmitting}>
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogTitle>{isEdit ? 'Editar Vínculo' : 'Novo Vínculo'}</DialogTitle>
        <DialogContent sx={{ paddingTop: '8px !important' }}>
          <Stack gap={3}>
            <Typography variant="body2" color="textSecondary">
              {isEdit
                ? 'Edite o vínculo entre Feeder e Master'
                : 'Selecione a pessoa Feeder e a Master para criar o vínculo.'}
            </Typography>

            <Stack gap={1.25}>
              <Typography variant="subtitle2" color="textPrimary">
                Feeder
              </Typography>
              {!isEdit && (
                <SearchAdd
                  service={services.pesquisas.pessoaFundo}
                  variant="empresas"
                  placeholder="Busque a pessoa Feeder"
                  onClick={pData => {
                    handleSelectPessoa('pessoa_id', pData);
                  }}
                />
              )}

              {renderSelected('feeder', 'Selecionado', formik.values.feeder, () =>
                handleClearPessoa('pessoa_id')
              )}
              {formik.touched.pessoa_id && formik.errors.pessoa_id && (
                <Typography variant="caption" color="error.main">
                  {formik.errors.pessoa_id}
                </Typography>
              )}
            </Stack>

            <Stack gap={1.25}>
              <Typography variant="subtitle2" color="textPrimary">
                Master
              </Typography>
              <SearchAdd
                service={services.pesquisas.pessoaFundo}
                params={{ master: true }}
                variant="empresas"
                placeholder="Busque a pessoa Master"
                onClick={pData => {
                  handleSelectPessoa('master_pessoa_id', pData);
                }}
              />
              {renderSelected('master', 'Selecionado', formik.values.master, () =>
                handleClearPessoa('master_pessoa_id')
              )}
              {formik.touched.master_pessoa_id && formik.errors.master_pessoa_id && (
                <Typography variant="caption" color="error.main">
                  {formik.errors.master_pessoa_id}
                </Typography>
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="primary"
            sx={{ px: 2 }}
            type="button"
            onClick={handleClose}
            disabled={formik.isSubmitting}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            sx={{ px: 2 }}
            type="submit"
            loading={formik.isSubmitting}
            disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}>
            Salvar vínculo
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
});

FeederMasterDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmitSuccess: PropTypes.func,
  onMessageSuccess: PropTypes.func,
  responseErrorHandling: PropTypes.func,
  offline: PropTypes.func,
  defaultValues: PropTypes.object
};

FeederMasterDialog.defaultProps = {
  open: false,
  onClose: () => {},
  onSubmitSuccess: () => {},
  onMessageSuccess: () => {},
  responseErrorHandling: () => {},
  offline: () => {},
  defaultValues: {}
};

FeederMasterDialog.displayName = 'FeederMasterDialog';

export default FeederMasterDialog;
